import { useState, useMemo, useCallback } from "react";
import { useSearch } from "@tanstack/react-router";
import { toast } from "sonner";

import { usePulseApiToken } from "@/features/auth/hooks/useApiToken";
import { useAuthStore } from "@/features/auth/store/useAuthStore";
import { useTokenManagement } from "@/hooks/useTokenManagement";
import { copyToClipboard } from "@/lib/clipboard";
import { MONITORING_TYPES } from "@/constants/monitoring";
import TwoColumnLayout from "@/components/Layout/TwoColumnLayout";
import { Skeleton } from "@/components/ui/skeleton";

import PulseHeader from "../components/Pulse/PulseHeader";
import PulseForm from "../components/Pulse/PulseForm";
import PulseBento from "../components/Pulse/PulseBento";
import { PulseSnapshotChanges } from "../components/Pulse/PulseSnapshotChanges";
import {
  useGetPulses,
  useGetPulsePostData,
  useGetPulseSnapshot,
  useRefreshPulseSnapshot,
  useDeletePulse,
  useUpdatePulse,
} from "../api/pulse.queries";
import type { PulseData } from "../types/pulse.types";

export default function PulseInfo() {
  const searchParams = useSearch({ from: "/pulse/info" });
  const pulseId = (searchParams as any)?.pulseId;

  const [showResults, setShowResults] = useState(false);

  const { apiToken, isPending: isApiTokenLoading } = usePulseApiToken();
  const { user } = useAuthStore();
  const { data: pulsesData, isLoading: isPulsesLoading } = useGetPulses();

  // Token management with dropdown functionality
  const {
    selectedToken,
    effectiveToken,
    handleTokenSelect,
    availableTokens,
    tokensLoading,
  } = useTokenManagement({
    apiToken,
    isApiTokenLoading,
    user,
    showTokenDropdown: true,
    additionalValidation: () => true,
    monitoringType: "pulse",
  });

  // Find the specific pulse
  const currentPulse = useMemo(() => {
    if (!pulsesData?.data || !pulseId) return null;
    return pulsesData.data.find((pulse: PulseData) => pulse._id === pulseId);
  }, [pulsesData, pulseId]);

  // Fetch post data for the current pulse
  const { data: postData, isLoading: isPostDataLoading } = useGetPulsePostData(
    currentPulse?.post_url || "",
    !!currentPulse?.post_url,
  );

  // Fetch snapshot data - disabled by default, only fetch on manual refresh
  const { data: snapshot, isLoading: isSnapshotLoading } = useGetPulseSnapshot(
    currentPulse?._id || "",
    false, // Disabled - only fetch when user clicks refresh
  );

  // Combined loading state - show full skeleton until ALL data is ready
  // This prevents the two-phase loading flicker
  const isLoadingPulse =
    isApiTokenLoading ||
    isPulsesLoading ||
    (!currentPulse && pulseId) ||
    (!!currentPulse && isPostDataLoading && !postData);

  const refreshSnapshot = useRefreshPulseSnapshot();
  const updatePulseMutation = useUpdatePulse();

  // Handlers
  const { mutate: deletePulseMutation, isPending: isDeletePulsePending } =
    useDeletePulse();
  const handleDelete = useCallback(() => {
    deletePulseMutation(currentPulse?._id || "");
  }, [deletePulseMutation, currentPulse?._id]);

  // const handleEdit = () => {
  //   navigate({ to: "/pulse/create", search: { mode: "edit" } });
  // };

  const handleRefresh = useCallback(() => {
    if (currentPulse?.post_url) {
      refreshSnapshot.mutate({
        pulseId: currentPulse._id,
        postUrl: currentPulse.post_url,
      });
    }
  }, [refreshSnapshot, currentPulse]);

  const handleCopyCode = useCallback(() => {
    copyToClipboard(JSON.stringify(postData, null, 2));
    toast.success("Copied to clipboard");
  }, [postData]);

  const handleExportJSON = useCallback(() => {
    const dataStr = JSON.stringify(postData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `pulse-${pulseId}-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [postData, pulseId]);

  const handleExportCSV = useCallback(() => {
    toast.info("CSV export coming soon");
  }, []);

  const handleUpdate = useCallback(() => {
    if (currentPulse?._id) {
      updatePulseMutation.mutate(currentPulse._id);
    }
  }, [updatePulseMutation, currentPulse?._id]);

  if (isLoadingPulse) {
    return (
      <TwoColumnLayout
        data={[]}
        isLoading={true}
        error={null}
        responseJSON="{}"
        apiEndpoint=""
        apiToken={effectiveToken}
        isApiTokenLoading={isApiTokenLoading}
        resultsTitle="Post Data JSON"
        showResults={false}
        selectedToken={selectedToken}
        availableTokens={availableTokens}
        tokensLoading={tokensLoading}
        onShowResults={setShowResults}
        onLaunch={() => {}}
        onCopyCode={() => {}}
        handleExportCSV={() => {}}
        handleExportJSON={() => {}}
        floatingButtonCondition={false}
        leftPanelMinSize={50}
        rightPanelMinSize={30}
        onPanelResize={() => {}}
        showTokenDropdown={true}
        onTokenSelect={handleTokenSelect}
        monitoringType={MONITORING_TYPES.PULSE}
      >
        <div className="flex h-full flex-col gap-5 overflow-hidden pr-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-10 w-64" />
          </div>
          <Skeleton className="h-[45px] w-full" />
          <div className="space-y-5">
            <Skeleton className="h-48 w-full rounded-lg" />
            <Skeleton className="h-64 w-full rounded-lg" />
          </div>
        </div>
      </TwoColumnLayout>
    );
  }

  if (!pulseId) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-neutral-600">No pulse selected</p>
      </div>
    );
  }

  const apiEndpoint = currentPulse?.post_url
    ? `${import.meta.env.VITE_API_BASE_URL}/pulse/post/${encodeURIComponent(currentPulse.post_url)}`
    : "";

  // Always show live postData in the Results Viewer, not snapshot data
  const displayData = postData;

  return (
    <TwoColumnLayout
      data={[]}
      isLoading={isPostDataLoading}
      error={null}
      responseJSON={JSON.stringify(displayData, null, 2)}
      apiEndpoint={apiEndpoint}
      apiToken={effectiveToken}
      isApiTokenLoading={isApiTokenLoading}
      resultsTitle="Post Data JSON"
      showResults={showResults}
      onShowResults={setShowResults}
      onLaunch={() => setShowResults(true)}
      onCopyCode={handleCopyCode}
      handleExportCSV={handleExportCSV}
      handleExportJSON={handleExportJSON}
      floatingButtonCondition={true}
      leftPanelMinSize={50}
      rightPanelMinSize={30}
      onPanelResize={(sizes) => {
        if (sizes[1] < 5) setShowResults(false);
      }}
      showTokenDropdown={true}
      onTokenSelect={handleTokenSelect}
      monitoringType={MONITORING_TYPES.PULSE}
      selectedToken={selectedToken}
      availableTokens={availableTokens}
      tokensLoading={tokensLoading}
    >
      <div className="flex h-full flex-col gap-5 overflow-hidden pr-3">
        <div className="flex items-center justify-between">
          <PulseHeader
            title={currentPulse?.pulse_name || "Pulse"}
            status={currentPulse?.status}
            lastUpdated={postData?.date_updated || "N/A"}
          />
        </div>

        <PulseForm
          postData={postData || null}
          snapshot={snapshot}
          // onEdit={handleEdit}
          onDelete={handleDelete}
          onRefresh={handleRefresh}
          isLoading={refreshSnapshot.isPending}
          isDeletePending={isDeletePulsePending}
          onUpdate={handleUpdate}
        />

        <div className="space-y-5">
          {/* Snapshot Changes Card */}
          <PulseSnapshotChanges
            snapshot={snapshot}
            isLoading={isSnapshotLoading}
          />

          {/* Post Details */}
          <PulseBento
            mainPulse={currentPulse}
            graphData={null}
            postData={postData?.data || null}
            onGraphDurationChange={(duration) =>
              console.log("Duration changed:", duration)
            }
            onShowJSON={() => setShowResults(true)}
          />
        </div>
      </div>
    </TwoColumnLayout>
  );
}
